import { useState } from 'react'
import { Zap, ArrowRight, RotateCw, Flag, LifeBuoy } from 'lucide-react'
import { useRetryPendingMessage, usePendingMessage, useTurnError } from '../../../../store'
import { useShowPaywall } from '@renderer/components/pricing/use-show-paywall'
import { restartOnboarding } from '@renderer/windows/features/onboarding/restart-onboarding'
import { useAuth } from '../../../../providers'
import { getChatApiRoute } from '../../../../api/v1Client'
import { logAmplitudeEvent } from '../../../../utils'
import { useContactSupport } from '@renderer/components/support/use-contact-support'
import type { ChatMessage } from '../../../../types'
import { Button } from '@highlight/ui'
import { MessageCardRequired } from './message-card-required'
import { CreditsOutReply } from './credits-out-reply'

export function MessageError({ message }: { message: ChatMessage }) {
  const [reportToDevs, setReportToDevs] = useState(false)
  const retryPendingMessage = useRetryPendingMessage()
  const pendingMessage = usePendingMessage()
  const { getAuthToken } = useAuth()
  const getHelp = useContactSupport('chat error')
  const turnError = useTurnError(message.conversation_id)
  const showPaywall = useShowPaywall()

  const handleReportToDevs = async () => {
    const conversation = message.conversation_id
    const errorMessage = message.error?.message || 'Unknown error, please check the console for more details.'
    const rayId = message.error?.rayId ?? 'Not available'
    const payload = { error: errorMessage, rayId }

    logAmplitudeEvent('Report Chat Error to Devs', {
      conversationId: conversation,
      messageRole: message.role === 'assistant' || message.role === 'user' ? message.role : 'user',
      errorMessage,
      rayId: message.error?.rayId,
    })

    try {
      const accessToken = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/conversations/${conversation}/error`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        console.error('Failed to report error to devs:', await response.text())
      }
    } catch (err) {
      console.error('Error reporting to devs:', err)
    }
  }

  if (!message.error) return null

  if (message.error.kind === 'CardRequired') {
    return <MessageCardRequired message={message} explanation={message.error.message ?? ''} />
  }

  // Out of credits isn't a fault to report: it gets the plan's reply instead.
  if (message.error.kind === 'CreditsExhausted') {
    return <CreditsOutReply />
  }

  return (
    <div className="mt-2 px-5">
      <div className="border-danger bg-danger/5 mb-3 flex w-full flex-col gap-2 rounded-2xl border p-4">
        <p className="text-danger-subtle text-sm font-semibold leading-none">Error</p>
        <p className="text-danger-subtle/80 m-0 overflow-hidden break-words text-sm">
          {message.error.message ?? 'An error occurred while processing your request.'}
        </p>
        <p className="text-danger-subtle/80 m-0 text-sm">
          You can report this issue to the Highlight team, or get help from support.
        </p>
        <div className="mt-2 flex w-fit items-center gap-2">
          {message.error.canRetry && message.id === pendingMessage?.id && (
            <Button size="sm" variant="primary" tone="danger" onClick={() => retryPendingMessage()}>
              <RotateCw size={16} />
              Retry
            </Button>
          )}
          {turnError?._tag === 'SubscriptionRequired' && (
            <Button size="sm" variant="primary" tone="danger" onClick={() => showPaywall('credits')}>
              <Zap size={16} />
              Choose a plan
            </Button>
          )}
          {turnError?._tag === 'OnboardingRequired' && (
            <Button size="sm" variant="primary" tone="danger" onClick={restartOnboarding}>
              <ArrowRight size={16} />
              Finish setup
            </Button>
          )}
          <Button
            size="sm"
            variant="primary"
            tone="danger"
            onClick={() => {
              handleReportToDevs()
              setReportToDevs(true)
            }}
            disabled={reportToDevs}
          >
            <Flag size={16} />
            {reportToDevs ? 'Reported' : 'Report Issue'}
          </Button>
          <Button size="sm" variant="primary" tone="danger" onClick={getHelp}>
            <LifeBuoy size={16} />
            Get Help
          </Button>
        </div>
      </div>
    </div>
  )
}
